import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { EventService } from "src/app/services/event.service";
import { IpqcPdfReportComponent } from "../ipqc-pdf-report/ipqc-pdf-report.component";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { ExportToCsv, Options } from "export-to-csv";
import * as moment from "moment";
import Swal from "sweetalert2";
import { IPQCMetadataDialougeComponent } from "../metadata-dialouge/metadata-dialouge.component";

interface DashboardTableData {
  "S.No": number;
  "Test ID": string;
  Date: string;
  "Battery ID": string;
  "BMS ID": string;
  "Test Status": string;
  "Test Result": string;
  Analytics: string;
  Download: string;
}

export interface TestPackData {
  pack_id: string;
  bms_id: string;
  product_id: string;
  variant_id: string;
  sop_flash: string;
  test_type: string;
}

interface DahboardTableData {
  test_id: string;
  created_at: string;
  pack_data: TestPackData;
  final_test_result: string;
  data: {
    [key: string]: string;
  };
}

@Component({
  selector: "app-piccolo-ipqc-dashboard-table",
  templateUrl: "./piccolo-ipqc-dashboard-table.component.html",
  styleUrls: ["./piccolo-ipqc-dashboard-table.component.css"],
})
export class PiccoloIpqcDashboardTableComponent implements OnInit {
  constructor(
    private eventService: EventService,
    private dialogue: MatDialog
  ) {}
  @Input() packId: string;
  @ViewChild(IpqcPdfReportComponent) ipqcPdfReport: IpqcPdfReportComponent;
  @ViewChild("tableFilterInput") fitlerInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  dashboardDataSource: MatTableDataSource<DashboardTableData> =
    new MatTableDataSource<DashboardTableData>([]);

  dashboardTableCols: string[] = [
    "S.No",
    "Test ID",
    "Battery ID",
    "BMS ID",
    "Date",
    // "Test Status",
    "Test Result",
    "Analytics",
    "Download",
  ];

  ngOnInit(): void {
    this.getPicoloIpqcDashboardTableData();
  }

  downloadCsvReport() {
    let testIds = Array.from(
      new Set(this.dashboardDataSource.filteredData.map((_) => _["Test ID"]))
    );

    if (testIds.length === 0) {
      Swal.fire({
        title: "No Test Data Found",
        text: "At lest one test data should be present to download report",
        icon: "warning",
      });
      return;
    }

    this.eventService.getIpqcCsvReportData("piccolo").subscribe({
      next: (res) => {
        if (res.status === 200 && res.body.success === true) {
          const validData = res.body.data?.filter((item) =>
            testIds.includes(item.test_id)
          );

          const exportOptions: Options = {
            useKeysAsHeaders: true,
            useTextFile: false,
            filename: `ipqc_report_${moment(new Date()).format("YYYY-MM-DD")}`,
          };

          const csvExporter = new ExportToCsv(exportOptions);
          csvExporter.generateCsv(validData);
        }
      },
    });
  }

  getPicoloIpqcDashboardTableData() {
    this.eventService.getPicoloIpqcTableDashboardData().subscribe({
      next: (response: { success: boolean; data: DahboardTableData[] }) => {
        if (response.success) {
          const dashboardData: DashboardTableData[] = response.data.map(
            (_, i) => {
              return {
                "S.No": i + 1,
                "Test ID": response.data[i].test_id,
                Date: moment(response.data[i].created_at).format("DD-MM-YYYY"),
                "Test Status": "Completed",
                Analytics: "Dialogue",
                Download: "PDF",
                "BMS ID": _?.pack_data?.bms_id ?? "---",
                "Battery ID": _?.pack_data?.pack_id ?? "---",
                "Test Result": _?.final_test_result ?? "---",
              };
            }
          );

          if (this.packId) {
            this.dashboardDataSource = new MatTableDataSource(
              dashboardData
                .filter((_) => _["Battery ID"] === this.packId)
                .map((_, i) => ({ ..._, "S.No": i + 1 }))
            );
          } else {
            this.dashboardDataSource = new MatTableDataSource(dashboardData);
          }

          this.dashboardDataSource.paginator = this.paginator;
        }
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  openDialog(testTableData: DashboardTableData) {
    interface TestMetaData {
      [key: string]: string | number;
    }

    const test_id = testTableData["Test ID"];

    this.eventService.getPiccoloIpqcTestMetaData(test_id).subscribe({
      next: (response: {
        success: boolean;
        data: { metaData: TestMetaData; packData: TestPackData };
      }) => {
        if (response.success) {
          const metaData = response.data.metaData;
          const packData = response.data.packData;

          this.dialogue.open(IPQCMetadataDialougeComponent, {
            height: "95%",
            width: "90%",
            maxWidth: "1250px",
            autoFocus: true,
            data: {
              date: testTableData["Date"],
              test_id: test_id,
              metaData: metaData,
              pack_data: packData,
              battery_type: "piccolo",
            },
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Something went wrong while fetching test meta data",
          });
        }
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  applyTableFilter() {
    const key = this.fitlerInput.nativeElement.value;
    this.dashboardDataSource.filter = key.trim().toLowerCase();
  }

  generateReportPdf(data: DashboardTableData) {
    const test_id = data["Test ID"];
    const date = data["Date"];

    this.eventService.getPiccoloIpqcTestMetaData(test_id).subscribe({
      next: (response: {
        success: boolean;
        data: {
          metaData: {
            [key: string]: string | number;
          };
          packData: TestPackData;
        };
      }) => {
        if (response.success) {
          const metaData = response.data.metaData;
          const packData = {
            ...response.data.packData,
            test_id,
            date,
            battery_type: "piccolo",
          };

          this.ipqcPdfReport.triggerPdfGeneration({ metaData, packData });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Something went wrong while fetching test meta data",
          });
        }
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  generateTestResultStyle(result: string) {
    let style = {
      textTransform: "capitalize",
    };

    if (result === "pass" || result === "Pass") {
      style["color"] = "green";
    } else if (result === "fail" || result === "Fail") {
      style["color"] = "red";
    }

    return style;
  }
}
